// src/pages/TopSongs.jsx
import { useEffect, useState } from "react";
import api, { API_BASE } from "../services/api";
import SmartImg from "../components/SmartImg";
import LazyMount from "../components/LazyMount";
import LiteSpotifyTrack from "../components/LiteSpotifyTrack";

export default function TopSongs() {
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTrackId, setActiveTrackId] = useState(null); // controls which track is loaded/playing

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/api/top5");
        setSongs(Array.isArray(data) ? data : []);
      } catch {
        setError("Top 5 is waiting for the backend API to come online.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="page">
        <Header />
        <section className="grid gap-4 md:grid-cols-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="card animate-pulse">
              <div className="flex gap-4">
                <div className="h-28 w-28 rounded-xl bg-white/5" />
                <div className="flex-1 space-y-2">
                  <div className="h-5 w-2/3 bg-white/5 rounded" />
                  <div className="h-4 w-full bg-white/5 rounded" />
                  <div className="h-4 w-1/2 bg-white/5 rounded" />
                  <div className="h-6 w-32 bg-white/5 rounded mt-2" />
                </div>
              </div>
            </div>
          ))}
        </section>
      </div>
    );
  }

  if (!songs.length) {
    return (
      <div className="page space-y-6">
        <Header />
        {error && (
          <section className="card border-amber-500/20 bg-amber-500/10 text-amber-100">
            {error}
          </section>
        )}
        <section className="card text-slate-300">
          No Top 5 yet. Check back soon.
        </section>
      </div>
    );
  }

  return (
    <div className="page space-y-8">
      <Header />
      <div className="space-y-8">
        {songs.map((s) => (
          <SongHeroCard
            key={s.rank ?? s.spotifyTrackId ?? s.title}
            s={s}
            activeTrackId={activeTrackId}
            setActiveTrackId={setActiveTrackId}
          />
        ))}
      </div>
    </div>
  );
}

/* ---------- Components ---------- */

function Header() {
  return (
    <section className="card">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-white">
            Top 5 Curated Songs of the Week
          </h1>
          <p className="text-slate-300 mt-1 whitespace-pre-line">
            <br />
            Our team has curated and chosen this week's top five standout tracks from artists we're working with!
          </p>
        </div>
      </div>
    </section>
  );
}

function SongHeroCard({ s, activeTrackId, setActiveTrackId }) {
  const cover = imgSrc(s.imageUrl);
  const isPlaying = s.spotifyTrackId && activeTrackId === s.spotifyTrackId;

  return (
    <section className="card p-6">
      <div className="grid gap-6 md:grid-cols-[180px_1fr] md:items-center">
        <HeroArt s={s} />
        <div>
          {/* Title + Rank */}
          <div className="flex items-center gap-3">
            {s.rank != null && <RankDisc rank={s.rank} size="lg" />}
            <h1 className="font-display text-3xl md:text-4xl leading-tight">
              {s.title} <span className="text-slate-300">- {s.artist}</span>
            </h1>
          </div>

          {/* Details */}
          {(s.overview || s.artistDetails) && (
            <div className="mt-4 grid gap-4 md:grid-cols-2">
              {s.overview && <InfoBlock title="Overview" text={s.overview} />}
              {s.artistDetails && <InfoBlock title="Artist" text={s.artistDetails} />}
            </div>
          )}

          {/* Spotify (lite, click-to-load, single active) */}
          {s.spotifyTrackId && (
            <div className="mt-5">
              <LazyMount>
                <LiteSpotifyTrack
                  id={s.spotifyTrackId}
                  title={`${s.title} — ${s.artist}`}
                  coverUrl={cover}
                  playing={isPlaying}
                  onPlayRequest={() => setActiveTrackId(s.spotifyTrackId)}
                />
              </LazyMount>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function InfoBlock({ title, text }) {
  return (
    <div>
      <h3 className="text-xs uppercase tracking-wider text-slate-400">{title}</h3>
      <p className="text-slate-300 mt-1">{text}</p>
    </div>
  );
}

function RankDisc({ rank, size = "md" }) {
  const circle = size === "lg" ? "h-12 w-12 text-base" : "h-9 w-9 text-sm";
  return (
    <div
      className={`${circle} grid place-items-center rounded-full bg-gradient-to-br from-brand-600/70 to-glow/60 text-white font-semibold shadow-card`}
      aria-label={`Rank ${rank}`}
    >
      #{rank}
    </div>
  );
}

function imgSrc(url) {
  if (!url) return null;
  return url.startsWith("http") ? url : `${API_BASE}${url}`;
}

function Art({ s, size = "sm" }) {
  const src = imgSrc(s.imageUrl);
  const cls = size === "sm" ? "h-28 w-28" : "h-44 w-44";
  return src ? (
    <SmartImg
      src={src}
      alt={s.title}
      className={`${cls} rounded-xl object-cover border border-white/10`}
      width={size === "sm" ? 112 : 176}
      height={size === "sm" ? 112 : 176}
    />
  ) : (
    <div
      className={`${cls} rounded-xl border border-white/10 bg-white/5 grid place-items-center text-[10px] text-slate-400`}
    >
      No image
    </div>
  );
}

function HeroArt({ s }) {
  return <Art s={s} size="lg" />;
}
